import { Link } from 'react-router-dom';
import { Crown, Lock } from 'lucide-react';
import { useEntitlement } from '../hooks/useEntitlement.js';

// ─────────────────────────────────────────────────────────────────
// Reusable plan guards for the editor. Mirrors the backend guards
// (billing/entitlement.ts): 'post' is quota-based (maxPosts on Free),
// 'email'/'component' are feature gates. The server enforces — these
// only drive button state + upgrade prompts.
// ─────────────────────────────────────────────────────────────────

export type CreateResourceKind = 'post' | 'email' | 'component';

export interface CreateGuardResult {
  loading: boolean;
  /** True when the active workspace may create this resource. */
  allowed: boolean;
  /** Human reason when blocked (drives the upgrade notice). */
  blockedReason?: string;
  plan: string;
}

const FEATURE_BY_RESOURCE: Record<Exclude<CreateResourceKind, 'post'>, 'emails' | 'components'> = {
  email: 'emails',
  component: 'components',
};

export function useCreateGuard(kind: CreateResourceKind): CreateGuardResult {
  const { data, isLoading } = useEntitlement();

  if (isLoading || !data) {
    return { loading: true, allowed: false, plan: 'free' };
  }

  if (kind === 'post') {
    const max = data.limits.maxPosts;
    const atLimit = max !== null && data.usage.posts >= max;
    return {
      loading: false,
      allowed: !atLimit,
      ...(atLimit
        ? { blockedReason: `The ${data.plan} plan allows ${max} posts — ${data.usage.posts}/${max} used.` }
        : {}),
      plan: data.plan,
    };
  }

  const feature = FEATURE_BY_RESOURCE[kind];
  const enabled = data.features[feature];
  return {
    loading: false,
    allowed: enabled,
    ...(enabled ? {} : { blockedReason: `${feature} are an Individual-plan feature.` }),
    plan: data.plan,
  };
}

/** Inline upgrade prompt shown next to a blocked create CTA. */
export function PlanUpgradeNotice({ reason }: { reason: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-line bg-bg/60 px-3 py-2 text-[12px] text-muted">
      <span className="flex items-center gap-1.5 font-medium text-ink-soft">
        <Lock size={12} className="text-accent" />
        {reason}
      </span>
      <Link
        to="/billing/plans"
        className="inline-flex items-center gap-1 font-semibold text-accent hover:underline"
      >
        <Crown size={12} />
        Upgrade on the Plan tab
      </Link>
    </div>
  );
}
